import React, { useState } from 'react';
import { Edit2, Trash2, Plus, DollarSign, Users, CheckCircle, Activity, Target } from 'lucide-react';

/**
 * TargetMetricsTable Component
 * Displays and manages metrics for a sales target in a table format
 */
const TargetMetricsTable = ({ 
  metrics = [], 
  onAdd, 
  onEdit, 
  onDelete, 
  editable = false,
  showProgress = true 
}) => {
  const [sortField, setSortField] = useState('MetricType');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedMetrics = [...metrics].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    if (sortField === 'PercentComplete') {
      aVal = a.TargetValue > 0 ? (a.CurrentValue / a.TargetValue) * 100 : 0;
      bVal = b.TargetValue > 0 ? (b.CurrentValue / b.TargetValue) * 100 : 0;
    }

    if (typeof aVal === 'string') {
      return sortDirection === 'asc' 
        ? aVal.localeCompare(bVal) 
        : bVal.localeCompare(aVal);
    }

    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const getMetricIcon = (type) => {
    switch (type) {
      case 'Revenue':
        return <DollarSign className="w-4 h-4" />;
      case 'NewCustomers':
        return <Users className="w-4 h-4" />;
      case 'DealsWon':
        return <CheckCircle className="w-4 h-4" />;
      case 'Activities':
        return <Activity className="w-4 h-4" />;
      default:
        return <Target className="w-4 h-4" />;
    }
  };

  const formatValue = (value, unit) => {
    if (unit === 'THB' || unit === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: unit || 'THB',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    }
    return new Intl.NumberFormat('en-US').format(value);
  };

  const getProgressColor = (percent) => {
    if (percent >= 100) return 'bg-green-500';
    if (percent >= 80) return 'bg-blue-500';
    if (percent >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusBadge = (percent) => {
    if (percent >= 100) return { label: 'Achieved', color: 'bg-green-100 text-green-800' };
    if (percent >= 80) return { label: 'On Track', color: 'bg-blue-100 text-blue-800' };
    if (percent >= 50) return { label: 'At Risk', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'Behind', color: 'bg-red-100 text-red-800' };
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <span className="text-gray-400">↕</span>;
    }
    return <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  if (metrics.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="text-center text-gray-500">
          <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-medium">No metrics defined</p>
          <p className="text-xs mt-1 mb-4">Add metrics to track progress toward this target</p>
          {editable && (
            <button
              onClick={onAdd}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Metric
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header with Add Button */}
      {editable && (
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-900">Target Metrics</h3>
          <button
            onClick={onAdd}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Metric
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('MetricType')}
              >
                <div className="flex items-center space-x-1">
                  <span>Metric</span>
                  <SortIcon field="MetricType" />
                </div>
              </th>
              {showProgress && (
                <>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('CurrentValue')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Current</span>
                      <SortIcon field="CurrentValue" />
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('TargetValue')}
                  >
                    <div className="flex items-center justify-end space-x-1">
                      <span>Target</span>
                      <SortIcon field="TargetValue" />
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('PercentComplete')}
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>Progress</span>
                      <SortIcon field="PercentComplete" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </>
              )}
              {!showProgress && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target Value
                </th>
              )}
              <th
                className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('Weight')}
              >
                <div className="flex items-center justify-center space-x-1">
                  <span>Weight</span>
                  <SortIcon field="Weight" />
                </div>
              </th>
              {editable && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedMetrics.map((metric) => {
              const percentComplete = metric.TargetValue > 0 
                ? (metric.CurrentValue / metric.TargetValue) * 100 
                : 0;
              const statusBadge = getStatusBadge(percentComplete);

              return (
                <tr key={metric.MetricID} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 flex items-center justify-center bg-gray-100 rounded">
                        {getMetricIcon(metric.MetricType)}
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {metric.MetricName || metric.MetricType}
                        </div>
                        <div className="text-xs text-gray-500">{metric.MetricType}</div>
                      </div>
                    </div>
                  </td>
                  
                  {showProgress && (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {formatValue(metric.CurrentValue || 0, metric.Unit)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                        {formatValue(metric.TargetValue, metric.Unit)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center">
                          <div className="w-full max-w-xs">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-gray-700">
                                {percentComplete.toFixed(1)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${getProgressColor(percentComplete)}`}
                                style={{ width: `${Math.min(percentComplete, 100)}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.color}`}>
                          {statusBadge.label}
                        </span>
                      </td>
                    </>
                  )}

                  {!showProgress && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatValue(metric.TargetValue, metric.Unit)}
                    </td>
                  )}

                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                    {metric.Weight || 1}x
                  </td>

                  {editable && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => onEdit(metric)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(metric.MetricID)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TargetMetricsTable;


